import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Menu, X, ChevronDown, LogOut, User, Briefcase, BarChart3 } from 'lucide-react';

const Header = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const navLinks = [
    { label: 'Securities', to: '/securities' },
    { label: 'MovieStocks', to: '/securities/movies' },
    { label: 'StarBonds', to: '/securities/stars' },
    { label: 'Forums', to: '/forum' },
    { label: 'Leaderboards', to: '/leaderboards' },
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const handleLogout = () => {
    logout();
    setDropdownOpen(false);
    navigate('/');
  };

  return (
    <header className="bg-[#0A0E17]/95 backdrop-blur-md border-b border-[#1E293B] sticky top-0 z-50" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="header-logo">
            <div className="w-9 h-9 bg-gradient-to-br from-[#F59E0B] to-[#D97706] rounded-lg flex items-center justify-center">
              <span className="text-black font-black text-sm">B₹</span>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-[#F8FAFC] font-bold text-sm leading-tight font-heading">Bollywood</h1>
              <p className="text-[#F59E0B] text-[10px] font-semibold tracking-[0.2em]">SENSEX</p>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link key={link.to} to={link.to}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.to) ? 'text-[#F59E0B] bg-[#F59E0B]/10' : 'text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#1E293B]/50'
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g,'-')}`}
              >{link.label}</Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {user ? (
              <div className="relative">
                <button onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 bg-[#121826] border border-[#1E293B] rounded-lg px-3 py-2 hover:border-[#F59E0B]/50 transition-all"
                  data-testid="user-menu-btn"
                >
                  <div className="w-7 h-7 rounded-full bg-[#F59E0B] flex items-center justify-center">
                    <span className="text-black font-bold text-xs">{user.username?.slice(0,2).toUpperCase()}</span>
                  </div>
                  <span className="text-[#F8FAFC] text-sm font-medium hidden sm:inline">{user.username}</span>
                  <span className="text-[#F59E0B] text-xs font-mono hidden md:inline">B₹{((user.cash_balance || 0) / 100000).toFixed(1)}L</span>
                  <ChevronDown className="w-4 h-4 text-[#64748B]" />
                </button>

                {dropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setDropdownOpen(false)} />
                    <div className="absolute right-0 top-full mt-2 w-52 bg-[#121826] border border-[#1E293B] rounded-xl overflow-hidden z-50 shadow-xl" data-testid="user-dropdown">
                      <div className="px-4 py-3 border-b border-[#1E293B]">
                        <p className="text-[#F8FAFC] font-semibold text-sm">{user.username}</p>
                        <p className="text-[#F59E0B] font-mono text-xs">B₹{(user.cash_balance || 0).toLocaleString()}</p>
                      </div>
                      <Link to={`/profile/${user.username}`} onClick={() => setDropdownOpen(false)} className="flex items-center gap-3 px-4 py-3 text-[#94A3B8] hover:bg-[#1E293B] hover:text-[#F8FAFC] transition-colors text-sm">
                        <User className="w-4 h-4" /> Profile
                      </Link>
                      <Link to="/portfolio" onClick={() => setDropdownOpen(false)} className="flex items-center gap-3 px-4 py-3 text-[#94A3B8] hover:bg-[#1E293B] hover:text-[#F8FAFC] transition-colors text-sm">
                        <Briefcase className="w-4 h-4" /> Portfolio
                      </Link>
                      <Link to="/leaderboards" onClick={() => setDropdownOpen(false)} className="flex items-center gap-3 px-4 py-3 text-[#94A3B8] hover:bg-[#1E293B] hover:text-[#F8FAFC] transition-colors text-sm">
                        <BarChart3 className="w-4 h-4" /> Leaderboards
                      </Link>
                      <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors text-sm w-full">
                        <LogOut className="w-4 h-4" /> Logout
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#1E293B]" data-testid="login-btn">Login</Button>
                </Link>
                <Link to="/register">
                  <Button className="bg-[#F59E0B] hover:bg-[#D97706] text-black font-semibold" data-testid="register-btn">Sign Up Free</Button>
                </Link>
              </>
            )}
            <button className="lg:hidden text-[#94A3B8] hover:text-[#F8FAFC]" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-btn">
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden py-4 border-t border-[#1E293B]" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link key={link.to} to={link.to} onClick={() => setMobileOpen(false)}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.to) ? 'text-[#F59E0B] bg-[#F59E0B]/10' : 'text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#1E293B]'
                  }`}
                >{link.label}</Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
